if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
      }

      this.filterByColor(this.dataset.selectedColor || '');
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    onSlideChanged(event) {
      if (!this.elements.thumbnails || !event.detail.currentElement) return;
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia || activeMedia.hidden) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }

    normalizeColor(value) {
      return String(value || '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
    }

    getColorTag(element) {
      const raw = element ? element.dataset.colorTags || '' : '';
      const match = raw.match(/\bcolor\s*:\s*([^,;|\n\r]+)/i);

      // Untagged media stays visible as shared content until a merchant adds color tags.
      return match ? this.normalizeColor(match[1]) : 'all';
    }

    isVisibleForColor(element, color) {
      const selectedColor = this.normalizeColor(color);
      const mediaColor = this.getColorTag(element);

      return !selectedColor || mediaColor === 'all' || mediaColor === selectedColor;
    }

    setMediaVisibility(element, isVisible) {
      element.hidden = !isVisible;
      element.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
      element.classList.toggle('product__media-item--color-hidden', !isVisible);
    }

    refreshSliders() {
      if (this.elements.viewer && this.elements.viewer.resetPages) this.elements.viewer.resetPages();
      if (this.elements.thumbnails && this.elements.thumbnails.resetPages) this.elements.thumbnails.resetPages();
    }

    getVisibleMediaItems() {
      return Array.from(this.elements.viewer.querySelectorAll('[data-media-id]')).filter((media) => !media.hidden);
    }

    filterByColor(color, preferredMediaId) {
      const mediaItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
      if (!mediaItems.length) return;

      const selectedColor = this.normalizeColor(color);
      this.dataset.currentColor = selectedColor;
      let visibleItems = mediaItems.filter((media) => this.isVisibleForColor(media, selectedColor));

      if (!visibleItems.length) {
        visibleItems = mediaItems;
      }

      mediaItems.forEach((media) => {
        this.setMediaVisibility(media, visibleItems.includes(media));
      });

      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
          const matchingMedia = mediaItems.find((media) => media.dataset.mediaId === thumbnail.dataset.target);
          const isVisible = matchingMedia ? !matchingMedia.hidden : this.isVisibleForColor(thumbnail, selectedColor);
          this.setMediaVisibility(thumbnail, isVisible);
        });
      }

      this.refreshSliders();

      const preferredItem = preferredMediaId ? visibleItems.find((media) => media.dataset.mediaId === preferredMediaId) : null;
      const activeItem = visibleItems.find((media) => media.classList.contains('is-active'));
      const nextItem = preferredItem || activeItem || visibleItems[0];

      if (nextItem) this.setActiveMedia(nextItem.dataset.mediaId, false);
    }
  });
}
